// @ts-check
import React from 'react';
import { Box, useTheme } from '../../pdf-design-system';

const TraitsContext = React.createContext({
  variant: 'default',
  /** @type {EmptyTraitComponent} */
  EmptyValue: () => null,
});
const TraitsProvider = TraitsContext.Provider;

/** @typedef {React.FunctionComponent<Parameters<import('./Trait').default>[0]>} EmptyTraitComponent */

/**
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {import('@react-pdf/types').Style} [props.style]
 * @param {string} [props.variant]
 * @param {EmptyTraitComponent} [props.EmptyValue]
 * @returns
 */
export default function Traits({
  children,
  style,
  variant = 'default',
  /** @type {React.FunctionComponent<Parameters<import('./Trait')>[0]} */
  EmptyValue = () => null,
  ...rest
}) {
  return (
    <TraitsProvider value={{ variant, EmptyValue }}>
      <TraitsContainer style={style} {...rest}>
        {children}
      </TraitsContainer>
    </TraitsProvider>
  );
}

/**
 * @typedef {| 'container' | 'section' | 'item' | 'label' | 'value'} TraitElement
 * @typedef {Partial<Record<TraitElement, import('@react-pdf/types').Style>>} TraitsStyles
 * @typedef {Record<string, TraitsStyles> &
 *  {
 *      layout?: TraitsStyles; // The layout without any particular styling.
 *      styles?: TraitsStyles; // The base styles.
 *  }
 * } TraitsDesign
 */
export function useTraits() {
  /** @type {{ theme: import('../../pdf-design-system/useTheme').BottlebooksPDFTheme["theme"] & {  components?: { Traits?: TraitsDesign; } } }} */
  const { theme } = useTheme();
  const { variant, EmptyValue } = React.useContext(TraitsContext);
  return {
    /** @param {keyof TraitsDesign[string]} item */
    styles(item) {
      return [
        theme.components?.Traits?.layout?.[item],
        theme.components?.Traits?.styles?.[item],
        theme.components?.Traits?.[variant]?.[item],
      ].filter(Boolean);
    },
    EmptyValue,
  };
}

/**
 *
 * @param {{
 *  children: React.ReactNode;
 *  style?: import('@react-pdf/types').Style
 * }} props
 * @returns
 */
function TraitsContainer({ children, style, ...rest }) {
  const { styles } = useTraits();
  return (
    <Box style={[...styles('container'), style]} {...rest}>
      {children}
    </Box>
  );
}

export function TraitsSection({ children, style = null, ...rest }) {
  const { styles } = useTraits();
  return (
    <Box style={[...styles('section'), style]} {...rest}>
      {children}
    </Box>
  );
}
Traits.Section = TraitsSection;

/**
 * Layout: Two-Column
 *
 *
 * @param {import('@react-pdf/types').Style} [section] The styles for each section. Defaults to equal-width columns by setting flexGrow to 1.
 * @returns {TraitsStyles}
 *
 * @example Layout:
 * |---------------------| |----------------------| <Trait.Section>
 * ||-------------------|| ||--------------------||
 * || ORIGIN        (1) || || CLASSIFICATION (4) ||   <Trait/>
 * || Leithaberg DAC    || || DAC                ||
 * ||-------------------|| ||--------------------||
 * || VARIETIES     (2) || || COLOR          (5) ||   <Trait/>
 * || Chardonnay        || || White              ||
 * ||-------------------|| ||--------------------||
 * || Alcohol       (3) || |----------------------| </Trait.Section>
 * || 14.5% Abv         ||
 * ||-------------------||
 * |---------------------|
 *
 * @example Equal distribution:
 * ```js
 * // Creates equally-sized columns (by defaulting to `{ flexGrow: 1 }`).
 * <Page theme={{ components: { Traits: { layout: multiColumnLayout() } } }}>
 *   <Traits>
 *    <Trait.Section style={{ width: '3cm', paddingRight: '5mm' }}>…</Trait.Section>
 *    <Trait.Section style={{ flexGrow: 1 }}>…</Trait.Section>
 *   </Traits>
 * </Page>
 * ```
 *
 * @example Auto sizing columns:
 * ```js
 * { layout: multiColumnLayout({}) };
 * // Adding {} as the param creates auto-sizing columns.
 * <Page theme={{ components: { Traits: { layout: multiColumnLayout({}) } } }}>
 *   <Traits>
 *     // Then add the desired widths to each column.
 *     <Trait.Section style={{ width: '3cm', paddingRight: '5mm' }}>…</Trait.Section>
 *     <Trait.Section style={{ flexGrow: 1 }}>…</Trait.Section>
 *   </Traits>
 * </Page>
 * ```
 *
 */
export const multiColumnLayout = (section = { flexGrow: 1 }) => ({
  container: { display: 'flex', flexDirection: 'row' },
  section,
});

/**
 * Layout: List
 * By default, renders label and value in a 1:2 ratio. Override via param styles.
 *
 * @param {import('@react-pdf/types').Style} [label] The styles for each label.
 * @param {import('@react-pdf/types').Style} [value] The styles for each value.
 * @returns {TraitsStyles}
 *
 * @example Layout:
 * |-----------------------------------------| <Trait.Section>
 * ||---------------------------------------||
 * || ORIGIN             Leithaberg DAC     ||   <Trait/>
 * ||---------------------------------------||
 * || VARIETIES          Chardonnay         ||   <Trait/>
 * ||---------------------------------------||
 * || Alcohol            14.5% Abv          ||   <Trait/>
 * ||---------------------------------------||
 * |-----------------------------------------| </Trait.Section>
 *
 * |-----------------------------------------| <Trait.Section>
 * ||---------------------------------------||
 * || CLASSIFICATION     DAC                ||   <Trait/>
 * ||---------------------------------------||
 * || COLOR              White              ||   <Trait/>
 * ||---------------------------------------||
 * |-----------------------------------------| </Trait.Section>
 *
 * @example 1:2 distribution:
 * ```jsx
 * // Creates default traits.
 * <Page theme={{ components: { Traits: { layout: listLayout() } } }}>
 *   <Traits>
 *    <Trait.Section> // Not necessary
 *      <Trait label="Alcohol">{product.alcohol}</Trait>
 *      <Trait label="Alcohol">{product.alcohol}</Trait>
 *    </Trait.Section>
 *    <Trait label="Residual Sugar">{product.residualSugar}</Trait>
 *   </Traits>
 * </Page>
 * ```
 *
 * @example custom sizes:
 * ```jsx
 * // Labels are fixed-widths, values use the remaining space.
 * <Page theme={{ components: { Traits: { layout: listLayout({ width: '3cm' }, { flexGrow: 2 }) } } }}>
 *   <Traits>
 *    <Trait.Section> // Not necessary
 *      <Trait label="Alcohol">{product.alcohol}</Trait>
 *      <Trait label="Alcohol">{product.alcohol}</Trait>
 *    </Trait.Section>
 *    <Trait label="Residual Sugar">{product.residualSugar}</Trait>
 *   </Traits>
 * </Page>
 * ```
 *
 */
export const listLayout = (label = { flex: '1' }, value = { flex: '2' }) => ({
  item: { display: 'flex', flexDirection: 'row' },
  label,
  value,
});

/**
 *
 * A layout wrapping across two columns.
 *
 * @param {import('@react-pdf/types').Style} [item] The styles for each label.
 * @returns {TraitsStyles}
 *
 * @example
 * |--------------------------------------------| <Trait.Section>
 * ||--------------------||--------------------||
 * || ORIGIN         (1) || VARIETIES      (2) ||   <Trait /> <Trait />
 * || Leithaberg DAC     ||   Chardonnay       ||
 * ||--------------------||--------------------||
 * || ALCOHOL        (3) |                      |   <Trait />
 * || 14.5% Abv          |                      |
 * ||--------------------|                      |
 * ||-------------------------------------------| </Trait.Section>
 *
 * ||-------------------------------------------|
 * ||--------------------||--------------------||
 * || CLASSIFICATION (4) || COLOR          (5) ||
 * || DAC                || White              ||
 * ||--------------------||--------------------||
 * ||-------------------------------------------|
 */
export const wrappingColumnLayout = (item = { width: '50%' }) => ({
  section: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  item,
});
